import { DatePicker, Tooltip, message } from "antd";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmojiPicker from 'emoji-picker-react';
import ProjectServices from "../../../Services/ProjectServices";
import TaskServices from "../../../Services/TaskServices";
import SocketService from "../../../Services/SocketService";
import DataParser from "../../../utlits/Dataparser";
import UserServices from "../../../Services/UserService";
import { FaUserPlus, FaRegCalendarAlt, FaPaperclip, FaPlus, FaTrashAlt, FaFlagCheckered } from "react-icons/fa";
import SprintServices from "../../../Services/SprintServices";
import DateField from "../../Reuseable/DateField";
import AssigneField from "../../Reuseable/AssigneField";
import LeadPendingField from "../../Reuseable/LeadPendingField";
import UserProfile from "../../Reuseable/UserProfile..jsx";
import InlineSubtaskRow from "../Tasks/InlineSubtaskRow";

const EditProject = (props) => {
    const {
        id,
        projectId,
        labelledby,
        reFreshData,
        closeBtnId,
        onCreateTask,
        onClose,
    } = props;

    const fileInputRef = useRef(null);
    const quillRef = useRef(null);
    const clickedLinkRef = useRef({ index: null, length: null });
    const descBlurTimeoutRef = useRef(null);
    const modalContainerRef = useRef(null);
    const [name, setName] = useState("");

    const [users, setUsers] = useState([]);
    const [owner, setOwner] = useState(null);
    const [status, setStatus] = useState("Planning");
    const [description, setDescription] = useState("");
    const [dueDate, setDueDate] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [newAttachments, setNewAttachments] = useState([]);
    const [showAllAttachments, setShowAllAttachments] = useState(false);
    const [sprints, setSprints] = useState([]);
    const [isSprintsLoading, setIsSprintsLoading] = useState(false);
    const [isAddingSprint, setIsAddingSprint] = useState(false);
    const [newSprintName, setNewSprintName] = useState("");
    const [newSprintDate, setNewSprintDate] = useState(null);
    const [newSprintDescription, setNewSprintDescription] = useState("");
    const [newSprintFiles, setNewSprintFiles] = useState([]);
    const sprintFileInputRef = useRef(null);

    const [tasks, setTasks] = useState([]);
    const [isTasksLoading, setIsTasksLoading] = useState(false);
    const [taskStatus, setTaskStatus] = useState([]);
    const [taskPriority, setTaskPriority] = useState([]);

    const [confirmModal, setConfirmModal] = useState({ show: false, title: "", content: "", onConfirm: null });
    const [inlineSubtaskParentId, setInlineSubtaskParentId] = useState(null);
    const [isAddingTask, setIsAddingTask] = useState(false);
    const MODAL_COL = { created: '110px', assigned: '110px', date: '120px', status: '120px', comment: '0px', project: '36px' };

    const showConfirm = (title, content, onConfirm) => {
        setConfirmModal({ show: true, title, content, onConfirm });
    };

    const closeConfirm = () => setConfirmModal(prev => ({ ...prev, show: false }));

    // Description field states
    const [isDescFocused, setIsDescFocused] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");

    const companyId = DataParser.CompanyId();

    // Modal-style dropdowns
    const [isOwnerMenuOpen, setIsOwnerMenuOpen] = useState(false);
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState("");

    const filteredUserOptions = users
        .map(user => ({ value: user.id, label: user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() }))
        .filter(option => option.label.toLowerCase().includes(userSearchTerm.toLowerCase()))
        .slice(0, 5);

    useEffect(() => {
        const fetchUsers = () => {
            UserServices.getProjectUsers(companyId)
                .then((res) => {
                    if (res.Good) {
                        setUsers(res.data.user || []);
                    }
                })
                .catch((err) => console.error("Error fetching users:", err));
        };
        fetchUsers();

        const handleClickOutside = (event) => {
            if (!event.target.closest('.owner-select-container')) setIsOwnerMenuOpen(false);
            if (!event.target.closest('.status-select-container')) setIsStatusMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [companyId]);

    // Fetch project detail + sprints immediately when projectId is available — no waiting for users
    const [resolvedOwnerId, setResolvedOwnerId] = useState(null);
    useEffect(() => {
        if (!projectId) return;
        setIsLoading(true);
        Promise.all([
            ProjectServices.getProjectDetail(projectId),
            SprintServices.getSprints(companyId, projectId),
        ])
            .then(([projectRes, sprintRes]) => {
                if (projectRes.Good) {
                    const project = projectRes.data;
                    setName(project.name);
                    setStatus(project.status);
                    setDescription(project.description || "");
                    setDueDate(project.due_date ? moment(project.due_date) : null);
                    setExistingAttachments(project.attachments || []);
                    setResolvedOwnerId(project.owner || null);
                    if (!project.owner) setOwner(null);
                }
                if (sprintRes.Good) {
                    setSprints(sprintRes.data || []);
                }
            })
            .catch(() => message.error("Failed to load project details"))
            .finally(() => setIsLoading(false));
    }, [projectId, companyId]);

    // Resolve owner name whenever users or the owner ID becomes available — covers both race directions
    useEffect(() => {
        if (!resolvedOwnerId || users.length === 0) return;
        const ownerUser = users.find(u => Number(u.id) === Number(resolvedOwnerId));
        if (ownerUser) {
            setOwner({
                value: ownerUser.id,
                label: ownerUser.full_name || `${ownerUser.first_name || ""} ${ownerUser.last_name || ""}`.trim() || `User ${ownerUser.id}`,
            });
        } else {
            setOwner({ value: resolvedOwnerId, label: "User " + resolvedOwnerId });
        }
    }, [users, resolvedOwnerId]);

    const fetchTasks = () => {
        setIsTasksLoading(true);
        ProjectServices.getProjectDetail(projectId).then(res => {
            if (res.Good && res.data) {
                setTasks(res.data.tasks || []);
                setTaskStatus(res.status || []);
                setTaskPriority(res.priority || []);
            }
        }).finally(() => setIsTasksLoading(false));
    };

    // Fetch tasks in the background after project data loads
    useEffect(() => {
        if (!projectId) return;
        fetchTasks();
    }, [projectId]);

    // Socket listeners — keep task table, priority and status lists in sync
    useEffect(() => {
        if (!projectId) return;
        SocketService.connect();

        const handleTaskUpdate = (payload) => {
            if (String(payload.company_id) !== String(companyId)) return;
            const { data } = payload;
            if (String(data?.project_id) === String(projectId) || String(data?.parent_id) === String(projectId)) {
                fetchTasks();
            }
        };

        const handlePriorityUpdate = (payload) => {
            if (String(payload.company_id) !== String(companyId)) return;
            const { action, data } = payload;
            setTaskPriority(prev => {
                if (action === "create") return [...prev, data];
                if (action === "update") return prev.map(p => p.id === data.id ? { ...p, ...data } : p);
                if (action === "delete") return prev.filter(p => p.id !== Number(data.id));
                return prev;
            });
        };

        const handleJobStatusUpdate = (payload) => {
            if (String(payload.company_id) !== String(companyId)) return;
            const { action, data } = payload;
            setTaskStatus(prev => {
                if (action === "create") return [...prev, data];
                if (action === "update") return prev.map(s => s.id === data.id ? { ...s, ...data } : s);
                if (action === "delete") return prev.filter(s => s.id !== Number(data.id));
                return prev;
            });
        };

        SocketService.on("task_update", handleTaskUpdate);
        SocketService.on("priority_update", handlePriorityUpdate);
        SocketService.on("jobstatus_update", handleJobStatusUpdate);

        return () => {
            SocketService.off("task_update", handleTaskUpdate);
            SocketService.off("priority_update", handlePriorityUpdate);
            SocketService.off("jobstatus_update", handleJobStatusUpdate);
        };
    }, [projectId, companyId]);

    const fetchSprints = () => {
        setIsSprintsLoading(true);
        SprintServices.getSprints(companyId, projectId)
            .then(res => {
                if (res.Good) setSprints(res.data || []);
            })
            .finally(() => setIsSprintsLoading(false));
    };

    const handleAddSprint = async (e) => {
        e.preventDefault();
        if (!newSprintName.trim() || !newSprintDate) {
            message.warning("Please enter sprint name and due date");
            return;
        }

        if (dueDate && newSprintDate.isAfter(dueDate, 'day')) {
            message.error(`Sprint date cannot be later than project due date (${dueDate.format("MMM D, YYYY")})`);
            return;
        }

        try {
            const res = await SprintServices.createSprint({
                name: newSprintName,
                due_date: newSprintDate.toISOString(),
                description: newSprintDescription,
                project_id: projectId,
                company_id: companyId,
                created_by: DataParser.UserId()
            });
            if (res.Good) {
                const createdSprintId = res.data.id;

                if (newSprintFiles.length > 0) {
                    for (const file of newSprintFiles) {
                        const formData = new FormData();
                        formData.append("file", file);
                        await SprintServices.uploadAttachment(createdSprintId, formData, { headers: { "Content-Type": "multipart/form-data" } });
                    }
                }

                message.success("Sprint created!");
                setNewSprintName("");
                setNewSprintDate(null);
                setNewSprintDescription("");
                setNewSprintFiles([]);
                setIsAddingSprint(false);
                fetchSprints();
            } else {
                message.error(res.data || "Failed to create sprint");
            }
        } catch (error) {
            message.error("Error creating sprint");
        }
    };

    const handleDeleteSprint = (sprintId) => {
        showConfirm(
            "Delete Sprint",
            "Are you sure? Tasks in this sprint will be moved to the general project pool.",
            async () => {
                try {
                    const res = await SprintServices.deleteSprint(sprintId);
                    if (res.Good) {
                        message.success("Sprint deleted");
                        fetchSprints();
                    }
                } catch (error) {
                    message.error("Failed to delete sprint");
                }
            }
        );
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setNewAttachments([...newAttachments, ...files]);
        e.target.value = null;
    };

    const removeNewAttachment = (index) => {
        setNewAttachments(newAttachments.filter((_, i) => i !== index));
    };

    const deleteExistingAttachment = async (attachmentId) => {
        showConfirm(
            "Remove File",
            "This will permanently delete this attachment.",
            async () => {
                try {
                    const res = await ProjectServices.deleteAttachment(attachmentId);
                    if (res.Good) {
                        setExistingAttachments(existingAttachments.filter(a => a.id !== attachmentId));
                        message.success("File removed");
                    }
                } catch (err) {
                    message.error("Failed to remove file");
                }
            }
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!name.trim()) {
            message.error("Project name is required");
            return;
        }

        if (!dueDate) {
            message.error("Due date is required for a project");
            return;
        }

        setIsSubmitting(true);
        const body = {
            name,
            owner: owner ? owner.value : null,
            status,
            description,
            due_date: dueDate.toISOString(),
        };

        try {
            const res = await ProjectServices.updateProject(projectId, body);
            if (res.Good) {

                if (newAttachments.length > 0) {
                    for (const file of newAttachments) {
                        const formData = new FormData();
                        formData.append("file", file);
                        await ProjectServices.uploadAttachment(projectId, formData, { headers: { "Content-Type": "multipart/form-data" } });
                    }
                }

                message.success("Project updated successfully!");
                const closeBtn = document.getElementById(closeBtnId || "projectEditClose");
                if (closeBtn) closeBtn.click();
                if (reFreshData) reFreshData();
            } else {
                message.error(res.data || "Failed to update project");
            }
        } catch (err) {
            console.error("Project update failed:", err);
            message.error("An error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteProject = () => {
        showConfirm(
            "Delete Project",
            "Are you sure you want to delete this project? Associated tasks will be decoupled.",
            async () => {
                try {
                    const res = await ProjectServices.deleteProject(projectId);
                    if (res.Good) {
                        message.success("Project deleted successfully");
                        const closeBtn = document.getElementById(closeBtnId || "projectEditClose");
                        if (closeBtn) closeBtn.click();
                        if (reFreshData) reFreshData();
                    }
                } catch (err) {
                    message.error("Deletion failed");
                }
            }
        );
    };

    return ReactDOM.createPortal(
        <>
            {/* Bootstrap-style confirm dialog — rendered over the edit project modal */}
            {confirmModal.show && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1065, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px", maxWidth: 420, width: '90%', background: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <div className="modal-header border-0 pb-1">
                            <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>{confirmModal.title}</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={closeConfirm} />
                        </div>
                        <div className="modal-body pt-2 pb-2">
                            <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "6px" }}>{confirmModal.content}</p>
                            <p style={{ fontSize: "13px", color: "#ff4d4f", marginBottom: 0 }}>
                                <i className="uil uil-exclamation-triangle me-1" />
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="modal-footer border-0 pt-1">
                            <button type="button" className="btn btn-light waves-effect" onClick={closeConfirm} style={{ borderRadius: "5px", fontSize: "13px" }}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger waves-effect waves-light"
                                style={{ borderRadius: "5px", fontSize: "13px" }}
                                onClick={() => { confirmModal.onConfirm?.(); closeConfirm(); }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div
                className="modal-overlay-custom"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 100008
                }}
                onClick={(e) => {
                    if (e.target.className === 'modal-overlay-custom') {
                        if (onClose) onClose();
                        else {
                            const closeBtn = document.getElementById(closeBtnId || "projectEditClose");
                            if (closeBtn) closeBtn.click();
                        }
                    }
                }}
            >
                <div
                    ref={modalContainerRef}
                    className="modal-content-custom"
                    style={{
                        width: '95%',
                        maxWidth: "1100px",
                        height: '95vh',
                        height: 'calc(var(--app-vh, 100vh) * 0.95)',
                        background: '#fff',
                        borderRadius: "24px",
                        border: "none",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        position: 'relative'
                    }}
                >
                    <div className="modal-header border-0 pb-0 d-flex justify-content-end align-items-center" style={{ padding: '16px 24px' }}>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => {
                                if (onClose) onClose();
                                else {
                                    const closeBtn = document.getElementById(closeBtnId || "projectEditClose");
                                    if (closeBtn) closeBtn.click();
                                }
                            }}
                            aria-label="Close"
                            id={closeBtnId || "projectEditClose"}
                            style={{ fontSize: '14px', backgroundSize: '12px', border: 'none', background: 'transparent' }}
                        >
                            <i className="uil uil-multiply" style={{ fontSize: '20px', color: '#A0A0A0' }} />
                        </button>
                    </div>

                    <div className="modal-body-custom" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0 40px 30px 40px' }}>

                        <style>{`
                .project-title-float-wrap {
                    position: relative;
                    margin-top: 12px;
                    margin-bottom: 4px;
                }
                .project-title-float-wrap input {
                    width: 100%;
                    border-radius: 12px !important;
                    height: 44px !important;
                    padding: 0 !important;
                    font-size: 17px !important;
                    font-weight: 500 !important;
                    border: 1.5px solid transparent !important;
                    transition: all 0.2s ease !important;
                    background: transparent;
                    outline: none;
                }
                .project-title-float-wrap input::placeholder {
                    color: #a0a0a0 !important;
                    font-size: 17px !important;
                    font-weight: 500 !important;
                }
                .project-title-float-wrap input:hover,
                .project-title-float-wrap input:focus {
                    border: 1.5px solid #1D1D1D !important;
                    padding: 0 16px !important;
                    box-shadow: none !important;
                    outline: none !important;
                }
                .project-title-float-wrap label {
                    position: absolute;
                    left: 16px !important;
                    top: 50% !important;
                    transform: translateY(-50%);
                    font-size: 17px !important;
                    opacity: 0 !important; 
                    transition: all 0.2s ease !important;
                    pointer-events: none;
                    background: #fff;
                    padding: 0 5px;
                    color: #1D1D1D;
                    z-index: 10;
                }
                /* Show label ONLY when focused */
                .project-title-float-wrap input:focus ~ label {
                    top: 0 !important;
                    font-size: 12px !important;
                    opacity: 1 !important;
                    transform: translateY(-50%);
                }
                .project-title-float-wrap input:focus::placeholder {
                    color: transparent !important;
                }

                .project-desc-float-wrap {
                    position: relative;
                    border-radius: 12px;
                    border: 1.5px solid transparent;
                    transition: all 0.2s ease;
                    margin-bottom: 8px;
                    margin-left: -1.5px; /* Offset the transparent border to align with title */
                }
                .project-desc-float-wrap:hover, .project-desc-float-wrap.focused { 
                    border-color: #1D1D1D !important; 
                    padding-left: 0 !important;
                    padding-right: 12px !important;
                    margin-left: 0;
                }
                .project-desc-float-wrap .desc-icon {
                    position: absolute;
                    left: 0;
                    top: 13px !important;
                    transform: none !important;
                    color: #a0a0a0;
                    transition: all 0.2s ease;
                    pointer-events: none;
                    z-index: 10;
                }
                .project-desc-float-wrap:hover .desc-icon,
                .project-desc-float-wrap.focused .desc-icon,
                .project-desc-float-wrap .ql-editor:not(.ql-blank) ~ .desc-icon {
                    opacity: 0 !important;
                }
                .project-desc-float-wrap label {
                    position: absolute;
                    left: 32px;
                    top: 12px !important;
                    transform: none !important;
                    font-size: 17px !important;
                    line-height: 20px !important;
                    opacity: 1 !important;
                    transition: all 0.2s ease !important;
                    pointer-events: none;
                    background: #fff !important;
                    padding: 0 5px;
                    color: #a0a0a0;
                    z-index: 99 !important;
                }
                .project-desc-float-wrap.focused label,
                .project-desc-float-wrap.has-value label {
                    top: 0 !important;
                    transform: translateY(-50%) !important;
                    font-size: 12px !important;
                    opacity: 1 !important;
                    left: 12px !important;
                    color: #1D1D1D !important;
                    background: #fff !important;
                    z-index: 99 !important;
                }
                .project-desc-float-wrap.has-value:not(.focused) label {
                    opacity: 0 !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                }

                .project-desc-float-wrap .ql-editor {
                    padding: 12px 0 8px 0 !important;
                    min-height: 44px !important;
                    max-height: 200px !important;
                    overflow-y: auto !important;
                    font-size: 15px !important;
                    transition: all 0.2s ease;
                }
                .project-desc-float-wrap .ql-editor p {
                    margin: 0 !important;
                    padding: 0 !important;
                }
                /* Redundant padding removed to prevent double-shift */
                .project-desc-float-wrap.focused .desc-icon,
                .project-desc-float-wrap.has-value .desc-icon {
                    opacity: 0 !important;
                }
                .project-desc-float-wrap .ql-editor.ql-blank::before {
                    display: none !important;
                }
                .project-desc-float-wrap .ql-container.ql-snow {
                    border: none !important;
                    font-family: inherit !important;
                }

                .desc-toolbar {
                    display: flex;
                    gap: 4px;
                    background: #F5F5F5;
                    padding: 2px 4px;
                    border-radius: 6px;
                    width: fit-content;
                    margin: 0 0 6px 0;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.2s ease;
                }
                .project-desc-float-wrap.focused .desc-toolbar {
                    opacity: 1;
                    visibility: visible;
                }
                .toolbar-btn {
                    background: transparent !important;
                    border: none !important;
                    padding: 0 !important;
                    border-radius: 4px;
                    color: #1D1D1D !important;
                    font-size: 14px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    width: 26px !important;
                    height: 26px !important;
                }
                .toolbar-btn i {
                    font-size: 14px !important;
                }
                .toolbar-btn:hover {
                    background: #E8E8E8 !important;
                }

                /* Meta-grid fields — mirrors EditTask modal layout */
                .proj-meta-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 4px 30px;
                    margin-bottom: 16px;
                }
                .proj-meta-item {
                    display: flex;
                    align-items: center;
                    min-height: 34px;
                }
                .proj-meta-label-wrap {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    width: 130px;
                    flex-shrink: 0;
                }
                .proj-meta-label-icon { font-size: 18px; color: #A0A0A0; }
                .proj-meta-label { font-size: 13px; color: #A0A0A0; font-weight: 500; white-space: nowrap; }
                .proj-meta-value { flex: 1; display: flex; align-items: center; }
                .proj-user-badge {
                    width: 18px; height: 18px; border-radius: 4px;
                    background: #0DDFAB; color: #fff; font-size: 8px;
                    display: flex; align-items: center; justify-content: center;
                    font-weight: 700; flex-shrink: 0;
                }
                .proj-add-task-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 14px;
                    border: 1.5px dashed #D1D5DB;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 600;
                    color: #A0A0A0;
                    background: transparent;
                    transition: all 0.2s;
                    margin-bottom: 16px;
                    user-select: none;
                }
                .proj-add-task-btn:hover { border-color: #0DDFAB; color: #0DDFAB; }

                .custom-user-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 250px;
                    background: #fff;
                    border: 1px solid #E8E8E8;
                    border-radius: 10px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    z-index: 10000;
                    margin-top: 5px;
                    overflow: hidden;
                }
                .search-header {
                    padding: 12px 16px;
                    border-bottom: 1px solid #F0F0F0;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .search-header i {
                    font-size: 18px;
                    color: #1a1a1a;
                }
                .search-header input {
                    border: none !important;
                    outline: none !important;
                    box-shadow: none !important;
                    font-size: 14px;
                    width: 100%;
                    color: #1D1D1D;
                    font-weight: 400;
                }
                .user-list-scroll {
                    padding: 5px;
                    max-height: 175px !important;
                    overflow-y: auto;
                }
                .user-option-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 10px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 11px;
                    color: #1a1a1a;
                }
                .user-option-item:hover {
                    background: #F5F5F5;
                }
                .user-avatar-mini {
                    width: 20px;
                    height: 20px;
                    border-radius: 4px;
                    background: #0DDFAB;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 9px;
                    font-weight: 700;
                    color: #fff;
                    flex-shrink: 0;
                }
                
                /* Task Listing Parity Styles */
                .subtask-count-badge {
                    background: #F2F2F2; 
                    color: #1d1d1d; 
                    padding: 2px 6px; 
                    border-radius: 6px; 
                    font-size: 11px; 
                    font-weight: 700;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    margin-left: 8px;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    flex-shrink: 0;
                }
                .subtask-count-badge:hover {
                    background: #1d1d1d !important;
                    color: #ffffff !important;
                }
                .task-row-actions-edit {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    margin-left: 10px;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.2s ease;
                }
                .task-row-edit:hover .task-row-actions-edit {
                    opacity: 1;
                    visibility: visible;
                }
                .action-icon-mini {
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                    background: #F8F9FA;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s;
                    border: none;
                    padding: 0;
                }
                .action-icon-mini:hover {
                    background: #E2E8F0;
                    color: #1d1d1d;
                }

                .attachment-chip {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 0 8px;
                    height: 24px;
                    background: #1D1D1D;
                    color: #0DDFAB;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 600;
                }
                
                .action-datepicker .ant-picker-input > input {
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    color: #1D1D1D !important;
                    cursor: pointer !important;
                }
                .action-datepicker .ant-picker-input > input::placeholder {
                    color: #A0A0A0 !important;
                }

                .link-popover {
                    background: #fff;
                    border: 1.5px solid #1D1D1D;
                    border-radius: 10px;
                    padding: 4px;
                    bottom: 50px !important;
                    left: 10px !important;
                    width: 320px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                    z-index: 10001;
                }
                
                .custom-dropdown-panel {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 200px;
                    background: #fff;
                    border: 1px solid #E8E8E8;
                    border-radius: 10px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    z-index: 10000;
                    margin-top: 5px;
                    overflow: hidden;
                    padding: 5px;
                }
                .option-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 12px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 11px;
                    color: #1D1D1D;
                    font-weight: 500;
                }
                .option-item:hover {
                    background: #F5F5F5;
                }

                .attachment-trigger {
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                    border: 1px solid #E8E8E8;
                }
                .attachment-trigger:hover { background: #f0f1f2; border-color: #adb5bd; }

                .btn-update-project {
                    background-color: #0DDFAB;
                    border: none;
                    border-radius: 5px;
                    height: 30px;
                    padding: 0 20px;
                    color: #fff;
                    font-weight: 700;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    box-shadow: none !important;
                    flex-shrink: 0 !important;
                }
                .btn-update-project:hover { background-color: #0DDFAB !important; color: #fff !important; }
                .btn-update-project:disabled { opacity: 0.6; cursor: not-allowed; }

                /* Generic Notched Floating Label for Sprint */
                .sprint-float-wrap {
                    position: relative;
                    margin-bottom: 12px;
                    border: 1px solid #E8E8E8;
                    border-radius: 10px;
                    transition: all 0.2s ease;
                }
                .sprint-float-wrap:focus-within {
                    border-color: #1D1D1D;
                }
                .sprint-float-wrap input, .sprint-float-wrap .ant-picker {
                    width: 100%;
                    border: none !important;
                    background: transparent !important;
                    box-shadow: none !important;
                    height: 48px !important;
                    padding: 0 16px !important;
                    font-size: 14px !important;
                    font-weight: 500 !important;
                }
                .sprint-float-wrap label {
                    position: absolute;
                    left: 16px !important;
                    top: 50% !important;
                    transform: translateY(-50%);
                    background: transparent;
                    padding: 0;
                    font-size: 14px;
                    font-weight: 500;
                    color: #A0A0A0;
                    transition: all 0.2s ease;
                    pointer-events: none;
                    z-index: 10;
                }
                .sprint-float-wrap:focus-within label,
                .sprint-float-wrap.has-value label {
                    top: 0 !important;
                    transform: translateY(-50%);
                    background: #fff;
                    padding: 0 5px;
                    font-size: 11px;
                    font-weight: 700;
                    color: #1D1D1D;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .sprint-float-wrap input::placeholder, 
                .sprint-float-wrap .ant-picker-input input::placeholder {
                    color: transparent !important;
                }
                .sprint-float-wrap:focus-within input::placeholder,
                .sprint-float-wrap.has-value input::placeholder {
                    color: #A0A0A0 !important;
                }

                .section-header {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1D1D1D;
                    margin-top: 12px;
                    margin-bottom: 4px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                /* Task Table Styles (Mirroring EditTask) */
                .task-list-header-edit {
                  display: flex; align-items: center; padding: 4px 0; background: #F2F2F2;
                }
                .task-list-header-edit span {
                  font-size: 13px; font-weight: 600; color: #212529; text-align: left; display: block;
                }
                .task-row-edit {
                  position: relative; display: flex; width: 100%; align-items: center; min-height: 30px; border-bottom: 1px solid #f5f5f5; padding: 0; transition: background-color 0.15s;
                }
                .task-row-edit:hover { background-color: #f9fafb !important; }
                .priority-indicator-edit {
                  flex-shrink: 0; align-self: stretch; width: 4px;
                  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                  display: flex; align-items: center; overflow: hidden;
                  cursor: default; border-radius: 0 4px 4px 0;
                }
                .priority-indicator-edit:hover { width: 60px; box-shadow: 2px 0 8px rgba(0,0,0,0.1); }
                .priority-indicator-edit span {
                  opacity: 0; transition: opacity 0.2s ease; white-space: nowrap;
                  font-size: 10px; font-weight: 700; color: #fff; padding-left: 6px;
                }
                .priority-indicator-edit:hover span { opacity: 1; transition-delay: 0.1s; }
                
                .subtask-user-name { font-weight: 500; color: #1D1D1D; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                
                .add-task-placeholder {
                    display: flex; align-items: center; gap: 10px; padding: 14px 16px; cursor: pointer;
                    border: 1px dashed #E8E8E8; border-radius: 12px; margin-top: 12px; transition: all 0.2s;
                }
                .add-task-placeholder:hover { border-color: #0DDFAB; background: #f0fffb; }
                .add-task-placeholder i { color: #0DDFAB; font-size: 18px; }
                .add-task-placeholder span { color: #1D1D1D; font-size: 14px; font-weight: 500; }
                /* Hide Scrollbars but keep functionality */
                .custom-comment-scroll::-webkit-scrollbar {
                    display: none !important;
                }
                .custom-comment-scroll {
                    -ms-overflow-style: none !important;
                    scrollbar-width: none !important;
                }
            `}</style>

                        {isLoading ? (
                            <div className="w-100 p-4">
                                <Skeleton height={44} style={{ marginBottom: '16px' }} />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                    <Skeleton height={44} />
                                    <Skeleton height={44} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                    <Skeleton height={44} />
                                    <Skeleton height={44} />
                                </div>
                                <Skeleton height={44} style={{ marginBottom: '12px' }} />
                                <Skeleton height={100} style={{ marginBottom: '12px' }} />
                                <Skeleton height={44} />
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    {/* Fixed Header Content */}
                                    <div className="fixed-header-content">
                                        {/* Notched Floating Label Name */}
                                        <div className="project-title-float-wrap">
                                            <input
                                                type="text"
                                                id="projectTitleInput"
                                                placeholder="Project Name"
                                                value={name}
                                                onChange={(e) => {
                                                    setName(e.target.value);
                                                }}
                                                className="shadow-none"
                                            />
                                            <label htmlFor="projectTitleInput">Project Name</label>
                                        </div>

                                        {/* Meta Fields — matches EditTask modal layout */}
                                        <div className="proj-meta-grid">
                                            {/* Project Owner */}
                                            <div className="proj-meta-item">
                                                <div className="proj-meta-label-wrap">
                                                    <span style={{ display:'inline-block', width:'16px', height:'16px', flexShrink:0, backgroundColor: '#A0A0A0', mask:"url('/assets/images/assignedtomodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/assignedtomodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                                    <span className="proj-meta-label">Project Owner:</span>
                                                </div>
                                                <div className="proj-meta-value">
                                                    <div className="owner-select-container position-relative">
                                                        <div
                                                            className="d-flex align-items-center gap-2"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => setIsOwnerMenuOpen(!isOwnerMenuOpen)}
                                                        >
                                                            {owner ? (
                                                                <>
                                                                    <div className="proj-user-badge">{owner.label.charAt(0).toUpperCase()}</div>
                                                                    <span style={{ fontSize: '11px', color: '#1D1D1D' }}>{owner.label}</span>
                                                                </>
                                                            ) : (
                                                                <span style={{ fontSize: '11px', color: '#A0A0A0' }}>Unassigned</span>
                                                            )}
                                                        </div>
                                                        {isOwnerMenuOpen && (
                                                            <div className="custom-user-dropdown">
                                                                <div className="search-header">
                                                                    <i className="uil uil-search" />
                                                                    <input
                                                                        placeholder="Search members..."
                                                                        value={userSearchTerm}
                                                                        onChange={(e) => setUserSearchTerm(e.target.value)}
                                                                        autoFocus
                                                                    />
                                                                </div>
                                                                <div className="user-list-scroll custom-comment-scroll">
                                                                    {filteredUserOptions.map(opt => (
                                                                        <div
                                                                            key={opt.value}
                                                                            className="user-option-item"
                                                                            onClick={() => {
                                                                                setOwner(opt);
                                                                                setIsOwnerMenuOpen(false);
                                                                                setUserSearchTerm("");
                                                                            }}
                                                                        >
                                                                            <div className="user-avatar-mini">{opt.label.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}</div>
                                                                            {opt.label}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Status */}
                                            <div className="proj-meta-item">
                                                <div className="proj-meta-label-wrap">
                                                    <i className="uil uil-process proj-meta-label-icon" />
                                                    <span className="proj-meta-label">Status:</span>
                                                </div>
                                                <div className="proj-meta-value">
                                                    <div className="status-select-container position-relative">
                                                        <div style={{ cursor: 'pointer' }} onClick={() => setIsStatusMenuOpen(!isStatusMenuOpen)}>
                                                            {(() => {
                                                                const cfg = {
                                                                    'Planning': { color: '#3498db', bg: '#EBF5FB' },
                                                                    'In Progress': { color: '#f39c12', bg: '#FEF9E7' },
                                                                    'On Hold': { color: '#95a5a6', bg: '#F2F3F4' },
                                                                    'Completed': { color: '#0DDFAB', bg: '#E8FDF8' },
                                                                    'Abandoned': { color: '#e74c3c', bg: '#FDEDEC' },
                                                                }[status] || { color: '#A0A0A0', bg: '#F5F5F5' };
                                                                return (
                                                                    <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
                                                                        {status || 'Set Status'}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </div>
                                                        {isStatusMenuOpen && (
                                                            <div className="custom-dropdown-panel">
                                                                {["Planning", "In Progress", "On Hold", "Completed", "Abandoned"].map(s => {
                                                                    const color = { 'Planning': '#3498db', 'In Progress': '#f39c12', 'On Hold': '#95a5a6', 'Completed': '#0DDFAB', 'Abandoned': '#e74c3c' }[s] || '#A0A0A0';
                                                                    return (
                                                                        <div key={s} className="option-item" onClick={() => { setStatus(s); setIsStatusMenuOpen(false); }}>
                                                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
                                                                            {s}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Due Date */}
                                            <div className="proj-meta-item">
                                                <div className="proj-meta-label-wrap">
                                                    <span style={{ display:'inline-block', width:'16px', height:'16px', flexShrink:0, backgroundColor: '#A0A0A0', mask:"url('/assets/images/duedatemodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/duedatemodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                                    <span className="proj-meta-label">Due Date:</span>
                                                </div>
                                                <div className="proj-meta-value" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span style={{ display:'inline-block', width:'16px', height:'16px', flexShrink:0, backgroundColor: '#0DDFAB', mask:"url('/assets/images/duedatemodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/duedatemodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                                    <DatePicker
                                                        bordered={false}
                                                        className="p-0"
                                                        value={dueDate}
                                                        onChange={setDueDate}
                                                        format="DD MMM"
                                                        placeholder="Set Due Date"
                                                        allowClear={false}
                                                        suffixIcon={null}
                                                        style={{ padding: 0, fontSize: '13px', color: '#1D1D1D', width: 'fit-content' }}
                                                        getPopupContainer={() => modalContainerRef.current || document.body}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className={`project-desc-float-wrap ${isDescFocused || showLinkInput || showEmojiPicker ? 'focused' : ''} ${(description && description !== '<p><br></p>') ? 'has-value' : ''}`}
                                            onClick={(e) => {
                                                const anchor = e.target.closest('a');
                                                if (anchor && anchor.closest('.ql-editor')) {
                                                    e.preventDefault();
                                                    const url = anchor.getAttribute('href');
                                                    setLinkUrl(url);
                                                    setShowLinkInput(true);

                                                    const q = quillRef.current.getEditor();
                                                    const Quill = ReactQuill.Quill;
                                                    const blot = Quill.find(anchor);
                                                    if (blot) {
                                                        clickedLinkRef.current = {
                                                            index: q.getIndex(blot),
                                                            length: blot.length()
                                                        };
                                                    }
                                                }
                                            }}
                                        >
                                            <span className="desc-icon" style={{ display:'inline-block', width:'18px', height:'18px', flexShrink:0, backgroundColor: '#a0a0a0', mask:"url('/assets/images/descriptionmodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/descriptionmodal.svg') no-repeat center / contain" }} />
                                            <ReactQuill
                                                ref={quillRef}
                                                theme="snow"
                                                value={description}
                                                onChange={setDescription}
                                                onFocus={() => {
                                                    if (descBlurTimeoutRef.current) {
                                                        clearTimeout(descBlurTimeoutRef.current);
                                                        descBlurTimeoutRef.current = null;
                                                    }
                                                    setIsDescFocused(true);
                                                }}
                                                onBlur={() => {
                                                    descBlurTimeoutRef.current = setTimeout(() => {
                                                        setIsDescFocused(false);
                                                    }, 150);
                                                }}
                                                placeholder=""
                                                modules={{ toolbar: false }}
                                            />
                                            <label>Description</label>

                                            {/* Custom Toolbar */}
                                            <div className="desc-toolbar" onMouseDown={(e) => e.preventDefault()}>
                                                <button type="button" className="toolbar-btn" onClick={() => {
                                                    const quill = quillRef.current.getEditor();
                                                    const format = quill.getFormat();
                                                    quill.format('bold', !format.bold);
                                                }}>
                                                    <i className="uil uil-bold" />
                                                </button>
                                                <button type="button" className="toolbar-btn" onClick={() => {
                                                    const quill = quillRef.current.getEditor();
                                                    const format = quill.getFormat();
                                                    quill.format('italic', !format.italic);
                                                }}>
                                                    <i className="uil uil-italic" />
                                                </button>
                                                <button type="button" className="toolbar-btn" onClick={() => {
                                                    const quill = quillRef.current.getEditor();
                                                    const format = quill.getFormat();
                                                    quill.format('list', format.list === 'ordered' ? false : 'ordered');
                                                }}>
                                                    <i className="uil uil-list-ui-alt" />
                                                </button>
                                                <button type="button" className="toolbar-btn" onClick={() => {
                                                    const quill = quillRef.current.getEditor();
                                                    const format = quill.getFormat();
                                                    quill.format('list', format.list === 'bullet' ? false : 'bullet');
                                                }}>
                                                    <i className="uil uil-list-ul" />
                                                </button>
                                                <div className="position-relative">
                                                    <button type="button" className="toolbar-btn" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                                                        <i className="uil uil-smile" />
                                                    </button>
                                                    {showEmojiPicker && (
                                                        <div className="position-absolute" style={{ bottom: '45px', left: '0', zIndex: 10000 }} onMouseDown={(e) => e.stopPropagation()}>
                                                            <EmojiPicker
                                                                onEmojiClick={(emojiData) => {
                                                                    const quill = quillRef.current.getEditor();
                                                                    const range = quill.getSelection();
                                                                    if (range) {
                                                                        if (range.length > 0) {
                                                                            quill.deleteText(range.index, range.length);
                                                                        }
                                                                        quill.insertText(range.index, emojiData.emoji);
                                                                        quill.setSelection(range.index + emojiData.emoji.length);
                                                                    } else {
                                                                        quill.insertText(quill.getLength(), emojiData.emoji);
                                                                    }
                                                                    setShowEmojiPicker(false);
                                                                }}
                                                                width={300}
                                                                height={400}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="position-relative">
                                                    <button type="button" className="toolbar-btn" onClick={() => {
                                                        const quill = quillRef.current.getEditor();
                                                        const format = quill.getFormat();
                                                        if (format.link) {
                                                            setLinkUrl(format.link);
                                                            const range = quill.getSelection();
                                                            if (range) {
                                                                const [leaf] = quill.getLeaf(range.index);
                                                                let blot = leaf;
                                                                const Quill = ReactQuill.Quill;
                                                                while (blot && blot.statics.blotName !== 'link' && blot !== quill.scroll) {
                                                                    blot = blot.parent;
                                                                }
                                                                if (blot && blot.statics.blotName === 'link') {
                                                                    clickedLinkRef.current = {
                                                                        index: quill.getIndex(blot),
                                                                        length: blot.length()
                                                                    };
                                                                } else {
                                                                    clickedLinkRef.current = { index: null, length: null };
                                                                }
                                                            }
                                                        } else {
                                                            setLinkUrl("");
                                                            clickedLinkRef.current = { index: null, length: null };
                                                        }
                                                        setShowLinkInput(!showLinkInput);
                                                    }}>
                                                        <i className="uil uil-link" />
                                                    </button>
                                                    {showLinkInput && (
                                                        <div className="position-absolute link-popover d-flex gap-2 align-items-center" style={{ bottom: '45px', left: '0' }} onMouseDown={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm shadow-none"
                                                                placeholder="Paste or type a link..."
                                                                value={linkUrl}
                                                                onChange={(e) => setLinkUrl(e.target.value)}
                                                                autoFocus
                                                            />
                                                            {linkUrl && (
                                                                <a
                                                                    href={linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="toolbar-btn d-flex align-items-center justify-content-center"
                                                                    style={{ padding: '4px 8px', borderRadius: '6px', border: '1.5px solid #1D1D1D', color: '#1D1D1D', textDecoration: 'none', background: '#fff', width: '32px', height: '32px' }}
                                                                    title="Open link in new tab"
                                                                >
                                                                    <i className="uil uil-external-link-alt" style={{ fontSize: '18px' }} />
                                                                </a>
                                                            )}
                                                            {linkUrl && (
                                                                <button
                                                                    type="button"
                                                                    className="toolbar-btn d-flex align-items-center justify-content-center"
                                                                    style={{ padding: '4px 8px', borderRadius: '6px', border: '1.5px solid #ff4d4f', color: '#ff4d4f', background: '#fff', width: '32px', height: '32px' }}
                                                                    title="Remove link"
                                                                    onClick={() => {
                                                                        const quill = quillRef.current.getEditor();
                                                                        const { index, length } = clickedLinkRef.current;
                                                                        if (index !== null && length !== null) {
                                                                            quill.formatText(index, length, 'link', false);
                                                                        } else {
                                                                            quill.format('link', false);
                                                                        }
                                                                        setShowLinkInput(false);
                                                                        setLinkUrl("");
                                                                        clickedLinkRef.current = { index: null, length: null };
                                                                    }}
                                                                >
                                                                    <i className="uil uil-link-broken" style={{ fontSize: '18px' }} />
                                                                </button>
                                                            )}
                                                            <button type="button" className="btn btn-sm btn-dark" onClick={(e) => {
                                                                e.preventDefault();
                                                                const quill = quillRef.current.getEditor();
                                                                const { index, length } = clickedLinkRef.current;
                                                                if (index !== null && length !== null) {
                                                                    quill.formatText(index, length, 'link', linkUrl);
                                                                } else {
                                                                    quill.format('link', linkUrl);
                                                                }
                                                                setShowLinkInput(false);
                                                                setLinkUrl("");
                                                                clickedLinkRef.current = { index: null, length: null };
                                                            }}>Apply</button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* Scrollable Middle Content */}
                                    <div className="custom-comment-scroll" style={{ flex: 1, overflowY: 'auto', paddingRight: '12px', margin: '0 -12px 0 0' }}>
                                        
                                        {/* Sprints Section */}
                                        <div className="section-header">Project Sprints</div>

                                        <div style={{ width: '100%', marginBottom: '24px' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <span style={{ fontSize: "12px", color: "#6b7280" }}>Manage milestones and execution phases</span>
                                                <button
                                                    type="button"
                                                    className="btn d-flex align-items-center gap-1 shadow-sm"
                                                    style={{
                                                        fontSize: "11px",
                                                        borderRadius: "6px",
                                                        backgroundColor: "#111827",
                                                        color: "#0DDFAB",
                                                        border: "none",
                                                        padding: "6px 12px",
                                                        fontWeight: '600'
                                                    }}
                                                    onClick={() => setIsAddingSprint(!isAddingSprint)}
                                                >
                                                    <FaPlus size={10} /> New Sprint
                                                </button>
                                            </div>

                                            {isAddingSprint && (
                                                <div className="mb-4 pt-2">
                                                    <div className="row g-3">
                                                        <div className="col-md-8">
                                                            <div className={`sprint-float-wrap ${newSprintName ? 'has-value' : ''}`}>
                                                                <label>Sprint Name</label>
                                                                <input
                                                                    type="text"
                                                                    value={newSprintName}
                                                                    onChange={(e) => setNewSprintName(e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className={`sprint-float-wrap ${newSprintDate ? 'has-value' : ''}`}>
                                                                <label>Due Date</label>
                                                                <DatePicker
                                                                    className="w-100"
                                                                    placeholder=""
                                                                    value={newSprintDate}
                                                                    onChange={setNewSprintDate}
                                                                    format="MMM D, YYYY"
                                                                    suffixIcon={<i className="uil uil-calendar-alt" style={{ color: '#0DDFAB' }} />}
                                                                    getPopupContainer={() => modalContainerRef.current || document.body}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-12 d-flex justify-content-end gap-2 mt-0">
                                                            <button type="button" className="btn btn-sm text-decoration-none text-muted fw-bold" style={{ fontSize: '12px' }} onClick={() => setIsAddingSprint(false)}>Cancel</button>
                                                            <button type="button" className="btn btn-sm shadow-sm" onClick={handleAddSprint} style={{ backgroundColor: "#1D1D1D", color: "#0DDFAB", borderRadius: "8px", fontWeight: "700", padding: "0 20px", height: '36px', border: 'none', fontSize: '12px' }}>Create Sprint</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="row g-2">
                                                {sprints.length === 0 ? (
                                                    <div className="text-center py-2 border" style={{ backgroundColor: "#F8F9FA" }}>
                                                        <p className="m-0 text-muted" style={{ fontSize: "13px" }}>No sprints defined yet.</p>
                                                    </div>
                                                ) : (
                                                    sprints.map(sprint => (
                                                        <div key={sprint.id} className="col-12">
                                                            <div className="p-3 border rounded-3 d-flex align-items-center justify-content-between hover-bg-light transition-all" style={{ background: '#fff' }}>
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "36px", height: "36px", backgroundColor: "#F8F9FA", color: "#0DDFAB" }}>
                                                                        <FaFlagCheckered size={14} />
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="m-0 fw-bold" style={{ fontSize: "13px", color: "#1D1D1D" }}>{sprint.name}</h6>
                                                                        <div className="d-flex align-items-center gap-2 mt-1">
                                                                            <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                                                                                <i className="uil uil-calendar-alt me-1" />
                                                                                {sprint.due_date ? moment(sprint.due_date).format("MMM D, YYYY") : "No date"}
                                                                            </span>
                                                                            <span className="badge" style={{ fontSize: '10px', background: '#F0FDF4', color: '#166534', borderRadius: '4px' }}>
                                                                                {sprint.status || 'Active'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex gap-2">
                                                                    <button type="button" className="btn btn-sm btn-light shadow-none border-0 p-2 text-danger" onClick={() => handleDeleteSprint(sprint.id)}>
                                                                        <i className="uil uil-trash-alt" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>

                                        {/* Task Table Section (Mirroring EditTask Subtask Table) */}
                                        <div className="section-header" style={{ marginTop: '24px' }}>Project Tasks</div>

                                        <div style={{ width: '100%' }}>
                                            <div className="task-list-header-edit">
                                                <div style={{ flex: 1, minWidth: 0, paddingLeft: '36px', display: 'flex', alignItems: 'center' }}><span>Task Title</span></div>
                                                <div style={{ width: '110px', flexShrink: 0, padding: '0 8px', display: 'flex', alignItems: 'center' }}><span>Created By</span></div>
                                                <div style={{ width: '110px', flexShrink: 0, padding: '0 8px', display: 'flex', alignItems: 'center' }}><span>Assigned to</span></div>
                                                <div style={{ width: '120px', flexShrink: 0, padding: '0 8px', display: 'flex', alignItems: 'center' }}><span>Due Date</span></div>
                                                <div style={{ width: '120px', flexShrink: 0, padding: '0 6px', display: 'flex', alignItems: 'center' }}><span>Status</span></div>
                                            </div>

                                            <div className="task-list-container-edit" style={{ minHeight: '40px' }}>
                                                {isTasksLoading ? (
                                                    <div style={{ padding: '8px 0' }}>
                                                        <Skeleton height={44} style={{ marginBottom: 8 }} />
                                                        <Skeleton height={44} style={{ marginBottom: 8 }} />
                                                        <Skeleton height={44} style={{ marginBottom: 8 }} />
                                                    </div>
                                                ) : (
                                                    <>
                                                        {tasks.map(task => {
                                                            const priorityObj = taskPriority?.find(p => String(p.id) === String(task.priority));

                                                            // Creator info (from task.task_assignee in projectController)
                                                            const creatorObj = task.task_assignee;
                                                            const creatorName = creatorObj ? `${creatorObj.first_name || ""} ${creatorObj.last_name || ""}`.trim() : "—";

                                                            // Assignee info (from task.task_assigned_to in projectController)
                                                            const assigneeObj = users?.find(u => String(u.id) === String(task.asigned_to)) || task.task_assigned_to;
                                                            const assigneeName = assigneeObj ? `${assigneeObj.first_name || ""} ${assigneeObj.last_name || ""}`.trim() : "Unassigned";

                                                            return (
                                                                <React.Fragment key={task.id}>
                                                                <div className="task-row-edit">
                                                                    {priorityObj?.color && (
                                                                        <div className="priority-indicator-edit" style={{ backgroundColor: priorityObj.color }}>
                                                                            <span>{priorityObj.name}</span>
                                                                        </div>
                                                                    )}

                                                                    {/* Task Title */}
                                                                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: priorityObj?.color ? '8px' : '12px', paddingRight: '8px', overflow: 'hidden' }}>
                                                                        <input
                                                                            className="form-check-input shadow-none"
                                                                            type="checkbox"
                                                                            style={{ borderRadius: '5px', cursor: 'pointer', flexShrink: 0, margin: 0, width: '16px', height: '16px' }}
                                                                            checked={task.status === 'Complete'}
                                                                            onChange={() => {
                                                                                const newStatus = task.status === 'Complete' ? 'Pending' : 'Complete';
                                                                                TaskServices.updateTaskStatus(task.id, { status: newStatus, company_id: companyId, company_identifier: DataParser.CompanyIdentifier() })
                                                                                    .then(res => { if (res.Good) fetchTasks(); });
                                                                            }}
                                                                        />
                                                                        <div style={{ minWidth: 0, overflow: 'hidden', flex: 1, display: 'flex', alignItems: 'center' }}>
                                                                            <span
                                                                                className="text-truncate"
                                                                                style={{
                                                                                    color: task.status === 'Complete' ? '#9ca3af' : '#111827',
                                                                                    fontSize: '13px',
                                                                                    fontWeight: '500',
                                                                                    textDecoration: task.status === 'Complete' ? 'line-through' : 'none',
                                                                                    cursor: 'pointer',
                                                                                    maxWidth: 'fit-content'
                                                                                }}
                                                                                onClick={() => {
                                                                                    if (props.onEditTask) props.onEditTask(task.id);
                                                                                }}
                                                                            >
                                                                                {task.title}
                                                                            </span>

                                                                            {/* Subtask Badge */}
                                                                            {task.subtask_count > 0 && (
                                                                                <span className="subtask-count-badge">
                                                                                    <span className="subtask-svg-icon" style={{ width: '16px', height: '16px', mask: "url('/assets/images/subtask.svg') no-repeat center / contain", WebkitMask: "url('/assets/images/subtask.svg') no-repeat center / contain" }} />
                                                                                    <span style={{ fontWeight: '400' }}>+{task.subtask_count}</span>
                                                                                </span>
                                                                            )}

                                                                            {/* Hover Actions */}
                                                                            <div className="task-row-actions-edit">
                                                                                <button type="button" className="action-icon-mini" title="Add Subtask" onClick={(e) => { e.stopPropagation(); setInlineSubtaskParentId(prev => prev === task.id ? null : task.id); }}>
                                                                                    <i className="uil uil-plus" style={{ fontSize: '14px' }} />
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="action-icon-mini"
                                                                                    title="Edit Task"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        if (props.onEditTask) props.onEditTask(task.id);
                                                                                    }}
                                                                                >
                                                                                    <i className="uil uil-edit-alt" style={{ fontSize: '14px' }} />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Created By */}
                                                                    <div style={{ width: '110px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', padding: '0 8px' }}>
                                                                        <UserProfile
                                                                            username={creatorName}
                                                                            userImage={creatorObj?.image}
                                                                            style={{ width: '20px', height: '20px', flexShrink: 0, borderRadius: '4px' }}
                                                                        />
                                                                        <span className="subtask-user-name" style={{ fontSize: '11px' }}>{creatorName.split(' ')[0]}</span>
                                                                    </div>

                                                                    {/* Assigned To */}
                                                                    <div style={{ width: '110px', flexShrink: 0, padding: '0 8px' }}>
                                                                        <AssigneField
                                                                            item={task}
                                                                            assigne={users}
                                                                            Services={TaskServices.updateTaskAssigne}
                                                                            Owner={assigneeObj}
                                                                            newOwner={task.asigned_to}
                                                                            localUpdate={fetchTasks}
                                                                            avatarSize="20px"
                                                                            textColor="#1d1d1d"
                                                                            styles={{ fontSize: '11px' }}
                                                                        />
                                                                    </div>

                                                                    {/* Due Date */}
                                                                    <div style={{ width: '120px', flexShrink: 0, padding: '0 8px' }}>
                                                                        <div className="d-flex align-items-center gap-1">
                                                                            <i className="uil-calendar-alt" style={{ fontSize: '20px', color: '#0DDFAB', flexShrink: 0 }} />
                                                                            <DateField
                                                                                item={task}
                                                                                defaultDate={task.due_date}
                                                                                Services={TaskServices.updateTaskDueDate}
                                                                                data={fetchTasks}
                                                                                styles={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500' }}
                                                                                suffixIcon={null}
                                                                                format="D, MMMM"
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    {/* Status */}
                                                                    <div style={{ width: '120px', flexShrink: 0, padding: '0 6px' }}>
                                                                        <LeadPendingField
                                                                            item={task}
                                                                            Status={taskStatus}
                                                                            DefaultStatus={task.status}
                                                                            Services={TaskServices.updateTaskStatus}
                                                                            data={fetchTasks}
                                                                            fontSize="10px"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {inlineSubtaskParentId === task.id && (
                                                                    <InlineSubtaskRow
                                                                        parentTask={{ id: task.id, due_date: task.due_date, project_id: projectId }}
                                                                        level={0}
                                                                        taskOwner={users}
                                                                        taskPriority={taskPriority}
                                                                        taskStatus={taskStatus}
                                                                        companyId={companyId}
                                                                        COL={MODAL_COL}
                                                                        onCreated={() => { setInlineSubtaskParentId(null); fetchTasks(); }}
                                                                        onCancel={() => setInlineSubtaskParentId(null)}
                                                                        minHeight="30px"
                                                                        padding="0"
                                                                    />
                                                                )}
                                                                </React.Fragment>
                                                            );
                                                        })}

                                                        {isAddingTask ? (
                                                            <InlineSubtaskRow
                                                                parentTask={{ id: 0, project_id: projectId, project_only: true }}
                                                                level={0}
                                                                taskOwner={users}
                                                                taskPriority={taskPriority}
                                                                taskStatus={taskStatus}
                                                                companyId={companyId}
                                                                COL={MODAL_COL}
                                                                onCreated={() => { setIsAddingTask(false); fetchTasks(); }}
                                                                onCancel={() => setIsAddingTask(false)}
                                                                minHeight="34px"
                                                                padding="1px 0"
                                                            />
                                                        ) : (
                                                            <div
                                                                className="add-subtask-placeholder"
                                                                onClick={(e) => { e.stopPropagation(); setIsAddingTask(true); }}
                                                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', cursor: 'pointer' }}
                                                            >
                                                                <div style={{ width: '18px', height: '18px', border: '1.5px solid #d1d5db', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', flexShrink: 0 }}>
                                                                    <i className="uil uil-plus" style={{ fontSize: '12px' }} />
                                                                </div>
                                                                <span style={{ color: '#9ca3af', fontSize: '12px', fontWeight: '500' }}>
                                                                    Click to add the task
                                                                </span>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                    </div>

                                    {/* Standardized Footer (Attachments & Update) - FIXED AT BOTTOM */}
                                    <div className="d-flex justify-content-between align-items-center pt-2 border-top" style={{ backgroundColor: '#fff', zIndex: 100 }}>
                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                            <div className="attachment-trigger cursor-pointer" onClick={() => fileInputRef.current?.click()} style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E8E8E8', borderRadius: '6px' }}>
                                                <span style={{ display:'inline-block', width:'16px', height:'16px', flexShrink:0, backgroundColor: '#1D1D1D', mask:"url('/assets/images/attachmentmodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/attachmentmodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                                <input type="file" ref={fileInputRef} className="d-none" multiple onChange={handleFileChange} />
                                            </div>

                                            {(() => {
                                                const chips = [
                                                    ...existingAttachments.map((att) => ({
                                                        key: `existing-${att.id}`,
                                                        type: 'existing',
                                                        name: att.attachment.split('-').slice(1).join('-') || att.attachment,
                                                        url: `${process.env.REACT_APP_PUBLIC_API_URL}/public/uploads/projects/${att.attachment}`,
                                                        onRemove: () => deleteExistingAttachment(att.id)
                                                    })),
                                                    ...newAttachments.map((file, idx) => ({
                                                        key: `new-${idx}`,
                                                        type: 'new',
                                                        name: file.name,
                                                        onRemove: () => removeNewAttachment(idx)
                                                    }))
                                                ];
                                                const visibleChips = showAllAttachments ? chips : chips.slice(0, 3);
                                                return (
                                                    <>
                                                        {visibleChips.map((chip) => (
                                                            <div
                                                                key={chip.key}
                                                                className="attachment-chip"
                                                                style={chip.type === 'new' ? { background: '#374151', border: '1px dashed #0DDFAB' } : {}}
                                                            >
                                                                <i className={chip.type === 'new' ? "uil uil-upload" : "uil uil-paperclip"} />
                                                                {chip.type === 'existing' ? (
                                                                    <a
                                                                        href={chip.url}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        style={{ color: 'inherit', textDecoration: 'none' }}
                                                                    >
                                                                        {chip.name}
                                                                    </a>
                                                                ) : (
                                                                    <span className="text-truncate" style={{ maxWidth: '150px' }}>{chip.name}</span>
                                                                )}
                                                                <i
                                                                    className={`uil ${chip.type === 'existing' ? 'uil-trash-alt' : 'uil-times'} remove-btn ms-2`}
                                                                    onClick={chip.onRemove}
                                                                    style={{ cursor: 'pointer', fontSize: '14px' }}
                                                                />
                                                            </div>
                                                        ))}
                                                        {chips.length > 3 && (
                                                            <div
                                                                className="attachment-chip cursor-pointer"
                                                                onClick={() => setShowAllAttachments(!showAllAttachments)}
                                                                style={{ background: '#f8f9fa', border: '1px dashed #adb5bd', color: '#1D1D1D' }}
                                                            >
                                                                <span style={{ fontWeight: 600 }}>
                                                                    {showAllAttachments ? "Show Less" : `+ ${chips.length - 3} more`}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-update-project"
                                            disabled={isSubmitting}
                                            style={{ height: '30px', padding: '0 20px', borderRadius: '5px', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            {isSubmitting && (
                                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                            )}
                                            Save Changes
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default EditProject;
